import type { QueryClient } from '@tanstack/react-query';
import type { Venue } from '../types';

// Query-cache entries holding venues: a paginated list (`{ items }`), an array, or one venue
const VENUE_QUERY_PREFIXES = [['venues'], ['venues-for-list'], ['concert-venue']];

/**
 * Show new flag values in every cached venue without refetching anything.
 *
 * A refetch would drop an unflagged venue from a "Show flagged" list while the contact overlay
 * pages through it, shifting its prev/next under the user. The entries are marked stale instead,
 * so each one catches up with the server the next time it is shown. See docs/VENUE_FLAGS.md.
 */
export function patchCachedVenueFlags(queryClient: QueryClient, venueIds: Iterable<string>, flagged: boolean): void {
  patchCachedVenueFields(queryClient, venueIds, { flagged });
}

/**
 * The same, for any venue field — the pause included (docs/VENUE_PAUSE.md).
 *
 * `patch` replaces the named fields on every cached copy of the listed venues; a venue already
 * carrying those values is left untouched, so React sees no new object and nothing re-renders.
 *
 * The lists are also marked stale, so each one catches up with the server the next time it is
 * shown — a flag or a pause decides whether a venue belongs in the list at all, which only the
 * server can answer. Pass `invalidate: false` for a field that no filter and no sort reads: the
 * patch already says everything such a list needs, so the stale mark would buy nothing but a
 * refetch nobody asked for, behind the Venues table's blocking "Loading..." scrim. That is why
 * `seen` opts out (docs/VENUE_SEEN.md).
 */
export function patchCachedVenueFields(
  queryClient: QueryClient,
  venueIds: Iterable<string>,
  patchFields: Partial<Venue>,
  { invalidate = true }: { invalidate?: boolean } = {},
): void {
  const ids = new Set(venueIds);
  const keys = Object.keys(patchFields) as (keyof Venue)[];
  const patchVenue = (venue: Venue): Venue =>
    venue && ids.has(venue._id) && keys.some((key) => venue[key] !== patchFields[key])
      ? { ...venue, ...patchFields }
      : venue;
  const patchList = (list: Venue[]): Venue[] => {
    const next = list.map(patchVenue);
    return next.some((venue, i) => venue !== list[i]) ? next : list;
  };
  const patch = (data: unknown): unknown => {
    if (Array.isArray(data)) return patchList(data);
    if (data && typeof data === 'object') {
      if ('items' in data && Array.isArray(data.items)) {
        const items = patchList(data.items);
        return items === data.items ? data : { ...data, items };
      }
      if ('_id' in data) return patchVenue(data as Venue);
    }
    return data;
  };
  for (const queryKey of VENUE_QUERY_PREFIXES) {
    queryClient.setQueriesData({ queryKey }, patch);
    if (invalidate) queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
  }
}

/** Refetch every cached venue after a bulk flag change, whose venues the client cannot list. */
export function refreshCachedVenues(queryClient: QueryClient): Promise<void[]> {
  return Promise.all(VENUE_QUERY_PREFIXES.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
}
